import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/shared/api/client';
import type { UserData } from '@/entities/user/types';
import type { AdminCompany, AdminCompanyUpdatePayload } from './types';

// The /admin/companies endpoint wraps results into { companies: [...] }, which
// is handy for future pagination but annoying for callers — we unwrap here so
// the hook just returns an array.
export const useAdminCompaniesQuery = () => {
  return useQuery<AdminCompany[]>({
    queryKey: ['admin', 'companies'],
    queryFn: async () => {
      const res = await apiClient.get<{ companies: AdminCompany[] }>('/admin/companies');
      return res.data?.companies ?? [];
    },
    staleTime: 30_000,
  });
};

export const useAdminUsersQuery = () => {
  return useQuery<UserData[]>({
    queryKey: ['admin', 'users'],
    queryFn: async () => {
      const res = await apiClient.get<{ users: UserData[] }>('/users?limit=1000');
      return res.data?.users ?? [];
    },
    staleTime: 30_000,
  });
};

export const useAdminUpdateCompanyMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: AdminCompanyUpdatePayload }) => {
      const res = await apiClient.put(`/admin/companies/${id}`, payload);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'companies'] });
      qc.invalidateQueries({ queryKey: ['user', 'me', 'companies'] });
    },
  });
};

export const useAdminSuspendCompanyMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, suspend }: { id: string; suspend: boolean }) => {
      const endpoint = suspend ? 'suspend' : 'resume';
      const res = await apiClient.put(`/admin/companies/${id}/${endpoint}`);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'companies'] });
      qc.invalidateQueries({ queryKey: ['user', 'me', 'companies'] });
    },
  });
};

export const useAdminDeleteCompanyMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.delete(`/admin/companies/${id}`);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'companies'] });
      qc.invalidateQueries({ queryKey: ['user', 'me', 'companies'] });
    },
  });
};

// Settings are edited under /admin/companies/:id/settings so admins can
// change the license plan / features / validity window of any company.
export const useAdminCompanySettingsQuery = (id: string | undefined) => {
  return useQuery({
    queryKey: ['admin', 'company-settings', id],
    queryFn: async () => {
      const res = await apiClient.get(`/admin/companies/${id}/settings`);
      return res.data;
    },
    enabled: Boolean(id),
    staleTime: 10_000,
  });
};

export const useAdminUpdateCompanySettingsMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: Record<string, unknown>;
    }) => {
      const res = await apiClient.put(`/admin/companies/${id}/settings`, payload);
      return res.data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['admin', 'companies'] });
      qc.invalidateQueries({ queryKey: ['admin', 'company-settings', variables.id] });
      qc.invalidateQueries({ queryKey: ['user', 'me', 'companies'] });
    },
  });
};
